import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Dialog from "@/components/Dialog";
import {
  AlgoliaQuickSearch,
  type FacilityHit,
  createFacilityOnlySearchUiState,
  getFacilityDisplayName,
  isFacilityExactMatch,
  renderFacilityOnlyHit
} from "@/components/AlgoliaSearch";
import { NotePriorityValues } from "@/types";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { FACILITY_GOVERNANCE_TITLES_QUERY } from "@/pages/facilities/[facilityId]/people/governance/queries";
import {
  buildGovernanceTitleOptions,
  getGovernanceTitleFieldState,
  GOVERNANCE_ROLE_NOTE_TITLE
} from "@/pages/facilities/[facilityId]/people/governance/_utils";
import { CREATE_FACILITY_GOVERNANCE_ROLE_MUTATION } from "@/pages/facilities/[facilityId]/people/governance/mutations";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import { canManageSectionCommittee } from "@/pages/sections/[sectionId]/operations/canManageSectionCommittee";
import { CREATE_GOVERNANCE_ROLE_MUTATION } from "@/pages/sections/[sectionId]/operations/_components/sectionGovernanceMutations";
import { GOVERNANCE_TITLES_QUERY } from "@/pages/sections/[sectionId]/operations/_components/sectionGovernanceQueries";
import { getSectionGovernanceTargetType } from "@/pages/sections/[sectionId]/operations/_components/getSectionGovernanceTargetType";
import useCapabilities, { DOMAIN, PERMISSION, useHasCapabilityBool, useHasRoles } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";

const facilitySearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

type OrganizationType = "facility" | "section";

type AllSectionsQueryData = {
  sections: Array<{
    id: string;
    name: string;
    legacyId?: string | null;
  }>;
};

function getAddGovernanceRoleHelpText(organizationType: OrganizationType | ""): string {
  if (organizationType === "section") {
    return "Choose a section and assign a governance role for this person.";
  }

  if (organizationType === "facility") {
    return "Search for a facility and assign a governance role for this person.";
  }

  return "Choose a facility or section and assign a governance role for this person.";
}

interface FallbackSection {
  id: string;
  name: string;
  legacyId?: string | null;
}

interface PersonAddGovernanceRoleModalProps {
  open: boolean;
  personId: string;
  canAddFacility: boolean;
  canAddSection: boolean;
  fallbackSections?: FallbackSection[];
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

interface FormValues {
  organizationType: OrganizationType | "";
  facilityId: string;
  sectionId: string;
  governanceTitleId: string;
  internalNote: string;
}

export function PersonAddGovernanceRoleModal({
  open,
  personId,
  canAddFacility,
  canAddSection,
  fallbackSections = [],
  onClose,
  refetchQueries = []
}: PersonAddGovernanceRoleModalProps) {
  const isGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const { sectionLegacyIds } = useCapabilities();
  const canViewSection = useHasCapabilityBool({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewSection });
  const [facilitySearchQuery, setFacilitySearchQuery] = useState("");
  const defaultOrganizationType: OrganizationType | "" =
    canAddFacility && canAddSection ? "" : canAddFacility ? "facility" : canAddSection ? "section" : "";
  const showOrganizationTypeField = canAddFacility && canAddSection;

  const [createFacilityGovernanceRole, { loading: savingFacility }] = useMutation(
    CREATE_FACILITY_GOVERNANCE_ROLE_MUTATION,
    { refetchQueries }
  );
  const [createGovernanceRole, { loading: savingSection }] = useMutation(CREATE_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries
  });
  const saving = savingFacility || savingSection;

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      organizationType: defaultOrganizationType,
      facilityId: "",
      sectionId: "",
      governanceTitleId: "",
      internalNote: ""
    }
  });

  const organizationType = watch("organizationType");
  const selectedFacilityId = watch("facilityId");
  const selectedSectionId = watch("sectionId");
  const selectedGovernanceTitleId = watch("governanceTitleId");

  const {
    data: facilityTitlesData,
    loading: facilityTitlesLoading,
    error: facilityTitlesError
  } = useQuery(FACILITY_GOVERNANCE_TITLES_QUERY, {
    fetchPolicy: "cache-first",
    skip: !open || organizationType !== "facility"
  });
  const {
    data: sectionsData,
    loading: sectionsLoading,
    error: sectionsError
  } = useQuery<AllSectionsQueryData>(ALL_SECTIONS_QUERY, {
    fetchPolicy: "cache-first",
    skip: !open || !canAddSection || !canViewSection
  });

  const sectionCatalog = useMemo(() => {
    const queriedSections = sectionsData?.sections ?? [];
    return queriedSections.length > 0 ? queriedSections : fallbackSections;
  }, [fallbackSections, sectionsData]);

  const sectionsLoadFailed = Boolean(sectionsError) && sectionCatalog.length === 0;

  const selectedSection = useMemo(
    () => sectionCatalog.find((section) => section.id === selectedSectionId),
    [sectionCatalog, selectedSectionId]
  );
  const sectionTargetType = getSectionGovernanceTargetType(selectedSection);

  const {
    data: sectionTitlesData,
    loading: sectionTitlesLoading,
    error: sectionTitlesError
  } = useQuery(GOVERNANCE_TITLES_QUERY, {
    variables: { targetType: sectionTargetType },
    fetchPolicy: "cache-first",
    skip: !open || organizationType !== "section" || !selectedSectionId
  });

  const sectionOptions = useMemo(() => {
    return sectionCatalog
      .filter((section) =>
        canManageSectionCommittee({
          isGlobalAdmin,
          assignedSectionLegacyIds: sectionLegacyIds,
          sectionLegacyId: section.legacyId
        })
      )
      .map((section) => ({
        value: section.id,
        label: section.name
      }));
  }, [isGlobalAdmin, sectionCatalog, sectionLegacyIds]);

  const governanceTitleOptions = useMemo(() => {
    if (organizationType === "facility") {
      return buildGovernanceTitleOptions(facilityTitlesData?.facilityGovernanceTitles);
    }
    return buildGovernanceTitleOptions(sectionTitlesData?.governanceTitles);
  }, [facilityTitlesData, organizationType, sectionTitlesData]);

  const titleFieldState = getGovernanceTitleFieldState({
    loading: organizationType === "facility" ? facilityTitlesLoading : sectionTitlesLoading,
    hasError: organizationType === "facility" ? Boolean(facilityTitlesError) : Boolean(sectionTitlesError),
    optionCount: governanceTitleOptions.length
  });

  useEffect(() => {
    if (!selectedGovernanceTitleId && governanceTitleOptions.length > 0) {
      setValue("governanceTitleId", governanceTitleOptions[0].value);
    }
  }, [governanceTitleOptions, selectedGovernanceTitleId, setValue]);

  const handleClose = () => {
    reset({
      organizationType: defaultOrganizationType,
      facilityId: "",
      sectionId: "",
      governanceTitleId: "",
      internalNote: ""
    });
    setFacilitySearchQuery("");
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (!personId) {
      return;
    }

    const content = values.internalNote.trim();
    const note = content ? { content, priority: NotePriorityValues.NONE, title: GOVERNANCE_ROLE_NOTE_TITLE } : null;

    try {
      if (values.organizationType === "facility") {
        const { data } = await createFacilityGovernanceRole({
          variables: {
            input: {
              personId,
              facilityId: values.facilityId,
              facilityGovernanceTitleId: values.governanceTitleId,
              note
            }
          }
        });

        if (!data?.createFacilityGovernanceRole.success) {
          showErrorSnackbar(data?.createFacilityGovernanceRole.message || "Unable to add governance role.");
          return;
        }
      } else if (values.organizationType === "section") {
        const { data } = await createGovernanceRole({
          variables: {
            input: {
              personId,
              governableId: values.sectionId,
              governableType: sectionTargetType,
              governanceTitleId: values.governanceTitleId,
              note
            }
          }
        });

        if (!data?.createGovernanceRole.success) {
          showErrorSnackbar(data?.createGovernanceRole.message || "Unable to add governance role.");
          return;
        }
      } else {
        return;
      }

      showSnackbar("Governance role added");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to add governance role.");
    }
  };

  const canSave =
    Boolean(personId) &&
    Boolean(selectedGovernanceTitleId) &&
    ((organizationType === "facility" && Boolean(selectedFacilityId)) ||
      (organizationType === "section" && Boolean(selectedSectionId)));

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Governance Role"
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} disabled={!canSave} loading={saving}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="text-body-medium text-sys-on-surface">{getAddGovernanceRoleHelpText(organizationType)}</p>

        {showOrganizationTypeField ? (
          <Controller
            name="organizationType"
            control={control}
            rules={{ required: "Type is required" }}
            render={({ field: { onChange, value }, fieldState }) => (
              <Select
                label="Type"
                options={[
                  { value: "facility", label: "Facility" },
                  { value: "section", label: "Section" }
                ]}
                value={value}
                onSelect={(nextValue) => {
                  onChange(nextValue);
                  setValue("facilityId", "");
                  setValue("sectionId", "");
                  setValue("governanceTitleId", "");
                  setFacilitySearchQuery("");
                }}
                error={fieldState.invalid}
                errorText={fieldState.error?.message}
                required
              />
            )}
          />
        ) : null}

        {organizationType === "facility" ? (
          <Controller
            name="facilityId"
            control={control}
            rules={{ required: "Facility is required" }}
            render={({ fieldState }) => (
              <AlgoliaQuickSearch<FacilityHit>
                indexName={facilitySearchIndexName}
                label="Facility"
                placeholder="Search by facility name or ID"
                defaultValue={facilitySearchQuery}
                initialUiState={createFacilityOnlySearchUiState(facilitySearchIndexName)}
                renderHit={renderFacilityOnlyHit}
                getDisplayValue={getFacilityDisplayName}
                isExactMatch={isFacilityExactMatch}
                required
                error={fieldState.invalid}
                errorText={fieldState.error?.message}
                noResultsMessage="No facilities found"
                onSelect={(hit) => {
                  setFacilitySearchQuery(getFacilityDisplayName(hit));
                  setValue("facilityId", hit.facility_id, { shouldValidate: true });
                }}
                onClear={() => {
                  setFacilitySearchQuery("");
                  setValue("facilityId", "");
                }}
              />
            )}
          />
        ) : null}

        {organizationType === "section" ? (
          <Controller
            name="sectionId"
            control={control}
            rules={{ required: "Section is required" }}
            render={({ field: { onChange, value }, fieldState }) => (
              <Select
                label="Section"
                options={sectionOptions}
                value={value}
                onSelect={(nextValue) => {
                  onChange(nextValue);
                  setValue("governanceTitleId", "");
                }}
                searchable={sectionOptions.length > 8}
                disabled={sectionsLoading || sectionsLoadFailed}
                error={fieldState.invalid || sectionsLoadFailed}
                errorText={
                  sectionsLoadFailed ? "Unable to load sections. Please try again." : fieldState.error?.message
                }
                required
              />
            )}
          />
        ) : null}

        {organizationType ? (
          <Controller
            name="governanceTitleId"
            control={control}
            rules={{ required: "Role is required" }}
            render={({ field: { onChange, value }, fieldState }) => (
              <Select
                label="Role"
                options={governanceTitleOptions}
                value={value}
                onSelect={onChange}
                disabled={titleFieldState.disabled || (organizationType === "section" && !selectedSectionId)}
                error={fieldState.invalid || Boolean(titleFieldState.errorText)}
                errorText={titleFieldState.errorText ?? fieldState.error?.message}
                supportingText={titleFieldState.supportingText}
                required
              />
            )}
          />
        ) : null}

        <Controller
          name="internalNote"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Internal Note (Optional)"
              value={value}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
